import InfiniteScroll from 'react-infinite-scroll-component';
import { Disclosure } from '@headlessui/react';
import { Role, rolesManipulator } from 'auth';
import { isEmpty } from 'lodash';
import debounce from 'lodash/debounce';
import { useCallback, useContext, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { withPrivateAccess } from '@/modules/auth/hocs/withPrivateAccess.tsx';
import { CreateReportModal } from '@/modules/common/components/modals/CreateReportModal.tsx';
import { ReportCard } from '@/modules/common/components/reports/ReportCard.tsx';
import { ReportFilters } from '@/modules/common/components/reports/ReportFilters.tsx';
import {
  ENTITY_ID_SEARCH_PARAM_KEY,
  REGISTER_ID_SEARCH_PARAM_KEY,
} from '@/modules/common/constants/search-params.ts';
import { useGetReportInfiniteList } from '@/modules/common/queries/reports.ts';
import { Button, EmptyListState, ModalProviderContext, PageLayout, SearchField, Spinner } from 'ui';
import { FiltersIcon } from 'ui/icons';
import { ReportFormat, ReportType } from 'api';

export interface ReportFilters {
  filename?: string;
  type?: ReportType;
  format?: ReportFormat;
  entity_id?: number;
}

const Reports = () => {
  const [searchParams] = useSearchParams();
  const entity_id = searchParams.get(ENTITY_ID_SEARCH_PARAM_KEY);
  const register_id = searchParams.get(REGISTER_ID_SEARCH_PARAM_KEY);
  const userRole = rolesManipulator.current_user_role;
  const isIssuer = userRole === Role.SR_ISSUER || userRole === Role.SR_ISSUER_ADMIN;

  const [filters, setFilters] = useState<ReportFilters>({});
  const { openModal } = useContext(ModalProviderContext);

  const {
    data,
    isLoading: isReportListLoading,
    hasNextPage,
    fetchNextPage,
  } = useGetReportInfiniteList({
    payload: {
      page: 1,
      page_size: 15,
      register_id: !register_id || isNaN(+register_id) ? undefined : +register_id,
      ...filters,
    },
  });

  const reports = (data?.pages || []).flatMap(({ data }) => data);

  const handleOpenCreateReportModal = useCallback(() => {
    openModal(CreateReportModal, {
      entityId: entity_id && Number(entity_id) ? +entity_id : undefined,
    });
  }, [entity_id, openModal]);

  const debouncedFiltersChange = useMemo(() => debounce(setFilters, 500), []);

  const handleSearch = useCallback(
    (filename: string) =>
      debouncedFiltersChange(filters => ({
        ...filters,
        filename,
      })),
    [debouncedFiltersChange],
  );

  const headerAction = (
    <div className="flex flex-wrap-reverse gap-2">
      <SearchField placeholder="Search" className="max-w-[200px]" onChange={handleSearch} />
      <Disclosure.Button>
        <div className="flex items-center h-[40px] border px-4 py-[0.5rem] rounded border-[rgba(23, 23, 23, 0.12)] gap-2">
          <img alt="filters" src={FiltersIcon} />
          <span className="text-sm">Filters</span>
        </div>
      </Disclosure.Button>
      <Button className="w-max whitespace-nowrap" onClick={handleOpenCreateReportModal}>
        Generate new report
      </Button>
    </div>
  );

  return (
    <PageLayout
      title="Reports"
      headerAction={headerAction}
      HeaderWrapper={Disclosure}
      headerBottomContent={
        <ReportFilters
          isLoading={isReportListLoading}
          handleFiltersChange={setFilters}
          isIssuer={isIssuer}
        />
      }
    >
      <EmptyListState
        title="No reports generated yet"
        description="You can generate reports from this page, click on Generate new report button to do it"
        isLoading={isReportListLoading}
        isSearchUsed={!isEmpty(filters)}
        itemsCount={reports?.length}
      />
      {isReportListLoading && !reports?.length && (
        <div className="w-full h-full flex items-center justify-center">
          <Spinner size="lg" />
        </div>
      )}
      {!!reports?.length && (
        <InfiniteScroll
          className="w-full h-full flex flex-col space-y-2"
          next={fetchNextPage}
          hasMore={!!hasNextPage}
          dataLength={reports.length}
          scrollableTarget="scrollable"
          loader={
            <div className="flex justify-center items-center h-16 w-full">
              <Spinner size="md" variant="primary" />
            </div>
          }
        >
          {reports?.map((report, index) => (
            <ReportCard report={report} key={`ReportCard_${report.id}_${index}`} />
          ))}
        </InfiniteScroll>
      )}
    </PageLayout>
  );
};

export default withPrivateAccess(Reports, [Role.SR_INVESTOR, Role.SR_ISSUER, Role.SR_ISSUER_ADMIN]);
